import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, startWith, Subject, switchMap } from 'rxjs';
import {
  BadgeComponent,
  BooleanPipe,
  DateTimeComponent,
  DetailsViewComponent,
  ImageThumbnailComponent,
  ImageThumbnailUnsupportedComponent,
  NoDataComponent,
  PrimengButtonDirective,
  SpinnerFilledComponent,
  ToastService,
} from '@web/shared/ui';
import {
  BoxQuestionnaireAvailabilityComponent,
  BoxQuestionnaireQuestion,
  BoxQuestionnaireQuestionOption,
  BoxQuestionnaireStatusComponent,
} from '@web/shared/module/shop';
import { Guid, isNotEmptyArray, isNotNil } from '@web/utils';
import { AdminBoxQuestionnaireDataSourceService } from '../../services';
import { Button } from 'primeng/button';

@Component({
  selector: 'app-admin-box-questionnaire-details',
  imports: [
    BadgeComponent,
    BooleanPipe,
    BoxQuestionnaireAvailabilityComponent,
    BoxQuestionnaireStatusComponent,
    DateTimeComponent,
    DetailsViewComponent,
    ImageThumbnailComponent,
    ImageThumbnailUnsupportedComponent,
    NgOptimizedImage,
    NoDataComponent,
    SpinnerFilledComponent,
    AsyncPipe,
    Button,
    PrimengButtonDirective,
  ],
  templateUrl: './admin-box-questionnaire-details.component.html',
  styleUrl: './admin-box-questionnaire-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBoxQuestionnaireDetailsComponent {
  public readonly id = input.required<Guid>();

  private readonly dataSource = inject(AdminBoxQuestionnaireDataSourceService);
  private readonly toast = inject(ToastService);

  private readonly refreshDetails$ = new Subject<void>();

  protected readonly details$ = combineLatest([
    toObservable(this.id),
    this.refreshDetails$.pipe(startWith(undefined)),
  ]).pipe(
    switchMap(([id]) => this.dataSource.fetchDetails(id)),
  );

  protected readonly isNotEmptyArray = isNotEmptyArray;
  protected readonly isNotNil = isNotNil;

  protected questionLabel(
    question: BoxQuestionnaireQuestion,
    index: number,
  ): string {
    return question.description?.trim() || `Wybór: Kosmetyk ${index + 1}`;
  }

  protected hasStateBadges(option: BoxQuestionnaireQuestionOption): boolean {
    return option.isSelected || option.isAutoSelected || !option.isAvailable;
  }

  protected hasDetails(option: BoxQuestionnaireQuestionOption): boolean {
    return isNotNil(option.remainingQuantity) || isNotNil(option.availableQuantity);
  }

  protected changeOptionSelection(questionnaireId: Guid, questionId: Guid, optionId: Guid): void {
    this.dataSource.confirmAndChangeSelection(questionnaireId, questionId, optionId).subscribe({
      next: () => this.refreshDetails(),
      error: () => this.showFailureToast(),
    });
  }

  private refreshDetails(): void {
    this.refreshDetails$.next();
  }

  private showFailureToast(): void {
    this.toast.show((builder) =>
      builder
        .initError(
          'Wystąpił błąd podczas zmiany wyboru.',
        )
        .build(),
    );
  }
}
